import { useTranslation } from "react-i18next";
import classNames from "classnames";
import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import Table from "./Table";
import { useCompanyCurrency, useCompanyDateFormat } from "../../../../../../utils/hooks/company";
import { currencyFormatter } from "../../../../../../utils/helpers/currencyFormatter";
import { renderDurationAsFormat } from "../../../../../../utils/helpers/date";
import { useSortRetros } from "./useSortRetros";

function Item({ date, isExpaned, setExpanded, data, primaryJob, columnVisibility, columns }) {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const currency = useCompanyCurrency();
    const original = data?.[date]?.original || [];
    const retros = data?.[date]?.retro || [];
    const diffTotal = retros?.filter((timeSheet) => {
        return moment(timeSheet.date).isSame(moment(date), "date") //||
            // !!timeSheet?.isNonProductiveTime;
    }).reduce((total, timeSheet) => total + timeSheet.total, 0);
    const diffHours = retros?.filter((timeSheet) => {
        if (timeSheet.hourType === "unpaid") return false;
        if (timeSheet.payCode.excludeFromPaidHours) return false;
        return moment(timeSheet.date).isSame(moment(date), "date");
    }).reduce((total, timeSheet) => total + timeSheet.duration, 0);
    const diffPremium = retros?.filter((timeSheet) => {
        if (timeSheet.hourType === "unpaid") return false;
        if (timeSheet.payCode.excludeFromPaidHours) return false;
        return moment(timeSheet.date).isSame(moment(date), "date");
    }).reduce((total, timeSheet) => total + timeSheet.otherDuration, 0);

    const sortedData = useSortRetros(original);
    const sortedRetros = useSortRetros(retros);

    return (
        <>
            <div
                className={classNames("d-flex align-items-center gap-x-4 border py-3 px-4 rounded-lg flex-wrap", !isExpaned && "mb-2")}
                style={{ background: isExpaned ? "#fff7ed" : "#fff" }}
            >
                <FontAwesomeIcon
                    className="cursor-pointer text-dark"
                    icon={isExpaned ? faMinus : faPlus}
                    onClick={() => setExpanded((prev) => ({ ...prev, [date]: !prev?.[date] }))}
                />
                <div className="text-dark mr-5">
                    <span className="font-weight-bold mr-2">{t("work-date")}:</span>
                    {moment(date).format(dateFormat)}
                </div>

                <div className="text-dark">
                    <span className="font-weight-bold mr-2">{t("adjustments")}:</span>
                    {retros?.length || 0}
                </div>

                <div className="d-flex gap-2">
                    <h4 className="m-0 p-0">{t("diff-hours")}:</h4>
                    <span>{renderDurationAsFormat(diffHours, "HH:mm", true, true)}</span>
                </div>
                <div className="d-flex gap-2">
                    <h4 className="m-0 p-0">{t("diff-premium-hours")}:</h4>
                    <span>{renderDurationAsFormat(diffPremium, "HH:mm", true, true)}</span>
                </div>
                <div className="d-flex gap-2">
                    <h4 className="m-0 p-0">{t("diff-amount")}:</h4>
                    <span>{currencyFormatter(diffTotal, 2, primaryJob?.currency || currency)}</span>
                </div>
            </div>

            {isExpaned && (
                <div className="p-2" style={{ background: "#f6f6f6" }}>
                    <div className="bg-white px-3 pt-3 pb-0">
                        <h3 className="text-info">
                            {t("original-entry")}
                        </h3>
                        <div className="mb-3 text-dark">
                            <span className="font-weight-bold mr-2">{t("work-date")}:</span>
                            {moment(date).format(dateFormat)}
                        </div>
                        <hr className="m-0" />
                        <Table
                            columns={columns}
                            data={sortedData}
                            footer={false}
                            columnVisibility={columnVisibility}
                        />
                    </div>

                    <div className="bg-white px-3 pb-3 pt-0">
                        <hr className="m-0 mb-3 p-0" style={{ borderStyle: "dashed" }} />
                        <h3 className="text-info">
                            {t("modified-entries")}
                        </h3>

                        <>
                            <div className="mb-1">
                                <div className="d-flex align-items-center gap-5 mb-3 text-dark">
                                    <div>
                                        <span className="font-weight-bold mr-2">{t("work-date")}:</span>
                                        {moment(date).format(dateFormat)}
                                    </div>
                                    <div>
                                        <span className="font-weight-bold mr-2">{t("adjustment-date")}:</span>
                                        {moment(retros?.[0]?.createdAt).format(dateFormat)}
                                    </div>
                                </div>
                                <hr className="m-0" />

                                <Table
                                    columns={columns}
                                    data={sortedRetros}
                                    footer={false}
                                    columnVisibility={columnVisibility}
                                />
                            </div>

                            <div className="bg-secondary py-3 px-4 rounded-lg">
                                <div className="d-flex gap-2">
                                    <h4 className="m-0 p-0">{t("diff-hours")}:</h4>
                                    <span>{renderDurationAsFormat(diffHours, "HH:mm", true, true)}</span>
                                </div>
                                <div className="d-flex gap-2">
                                    <h4 className="m-0 p-0">{t("diff-premium-hours")}:</h4>
                                    <span>{renderDurationAsFormat(diffPremium, "HH:mm", true, true)}</span>
                                </div>
                                <div className="d-flex gap-2">
                                    <h4 className="m-0 p-0">{t("diff-amount")}:</h4>
                                    <span>{currencyFormatter(diffTotal, 2, primaryJob?.currency || currency)}</span>
                                </div>
                            </div>
                        </>
                    </div>
                </div>
            )}
        </>
    )
}

export default Item;
